import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Clock, CheckCircle, Briefcase, Heart, Sparkles, Leaf } from 'lucide-react';

const typeConfig = {
  therapist: {
    label: 'Terapeuta',
    badgeClass: 'badge-therapist',
    icon: Heart
  },
  coach: {
    label: 'Mentor',
    badgeClass: 'badge-coach',
    icon: Briefcase
  },
  spiritualist: {
    label: 'Espiritualista',
    badgeClass: 'badge-spiritualist',
    icon: Sparkles
  }
};

export default function ProfessionalCard({ professional, showMatchReason = false }) {
  const config = typeConfig[professional.professional_type] || typeConfig.therapist;
  const TypeIcon = config.icon;

  return (
    <Link 
      to={`/professionals/${professional.id}`}
      className="card-theraflow block group"
      data-testid={`professional-card-${professional.id}`}
    >
      <div className="p-6 md:p-8">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            {professional.photo_url ? (
              <img
                src={professional.photo_url}
                alt={professional.name}
                className="w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover"
              />
            ) : (
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-[#F6EBE6] flex items-center justify-center">
                <TypeIcon className="w-8 h-8 text-[#4A5D4E]" strokeWidth={1.5} />
              </div>
            )}
            {professional.verified && (
              <div className="absolute -bottom-1 -right-1 bg-[#6B8E7C] rounded-full p-1">
                <CheckCircle className="w-4 h-4 text-white" strokeWidth={2} />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.badgeClass}`}>
                <TypeIcon className="w-3 h-3" strokeWidth={1.5} />
                {config.label}
              </span>
            </div>
            
            <h3 className="text-lg md:text-xl font-medium text-[#292927] truncate group-hover:text-[#4A5D4E] transition-colors" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {professional.name}
            </h3>

            {/* Rating */}
            <div className="flex items-center gap-2 mt-1">
              <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-4 h-4 ${
                      star <= Math.round(professional.rating_avg || 0)
                        ? 'text-[#D4A373] fill-[#D4A373]'
                        : 'text-[#EAE8E3]'
                    }`}
                    strokeWidth={1.5}
                  />
                ))}
              </div>
              <span className="text-sm text-[#63635E]">
                {professional.rating_avg?.toFixed(1) || '0.0'} ({professional.rating_count || 0})
              </span>
            </div>
          </div>
        </div>

        {/* Specializations */}
        {professional.specializations?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {professional.specializations.slice(0, 4).map((spec, idx) => (
              <span 
                key={spec}
                className="px-3 py-1 bg-[#F6EBE6] text-[#63635E] text-xs rounded-full"
              >
                {spec}
              </span>
            ))}
            {professional.specializations.length > 4 && (
              <span className="px-3 py-1 text-[#A0A09A] text-xs">
                +{professional.specializations.length - 4}
              </span>
            )}
          </div>
        )}

        {/* Bio */}
        {professional.bio && (
          <p className="text-sm text-[#63635E] mt-4 line-clamp-2">
            {professional.bio}
          </p>
        )}

        {/* Match Reason (for AI matches) */}
        {showMatchReason && professional.match_reason && (
          <div className="mt-4 p-3 bg-[#F6EBE6]/50 rounded-xl">
            <p className="text-sm text-[#4A5D4E] flex items-start gap-2">
              <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              {professional.match_reason}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#EAE8E3]">
          <div className="flex items-center gap-2 text-sm text-[#63635E]">
            <Clock className="w-4 h-4" strokeWidth={1.5} />
            {professional.experience_years} anos de experiência
          </div>
          {professional.hourly_rate && (
            <span className="text-sm font-medium text-[#292927]">
              R$ {professional.hourly_rate?.toFixed(0)}/hora
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
